/**
 * The wizard's own tiny client for /api/v1/setup — plain fetch, because the
 * four apps that host it have four different API clients (and two have none),
 * and the wizard runs BEFORE any of them holds a session.
 */

export type SetupBusinessType = 'restaurant' | 'retail' | 'salon' | 'laundry';

export interface SetupStateResponse {
  tenant_slug: string;
  tenant_name: string;
  username: string;
  /** 'reconfigure' = an admin re-running the wizard from the admin app. */
  mode?: 'onboarding' | 'reconfigure';
  business_type?: SetupBusinessType | null;
  draft: unknown;
  /** The answers the last completed run applied (SetupCompletePayload shape). */
  previous?: unknown;
  current: {
    name_ar: string | null;
    country_code: string | null;
    currency_code: string | null;
    logo: string | null;
    branch: {
      name: string | null;
      name_ar: string | null;
      city: string | null;
      street: string | null;
      building: string | null;
    } | null;
  };
  reference: {
    business_types: Array<{
      key: SetupBusinessType;
      default_modules: string[];
      optional_modules: string[];
    }>;
    modules: string[];
    countries: Array<{ code: string; name: string; name_ar: string; dial_code: string; currency: string }>;
    currencies: Array<{ id: number; code: string; name: string; name_ar: string }>;
  };
}

export interface SetupCompletePayload {
  business_type: SetupBusinessType;
  business: {
    name_ar: string | null;
    logo: string | null;
    country_code: string;
    currency_code: string;
    phone: string | null;
    email: string | null;
    address_en: string | null;
    address_ar: string | null;
    vat: {
      enabled: boolean;
      rate: number;
      prices_include_vat: boolean;
      vat_number: string | null;
      commercial_no: string | null;
    } | null;
  };
  branch: {
    name: string;
    name_ar: string | null;
    city: string | null;
    street: string | null;
    building: string | null;
    phone: string | null;
  };
  modules: string[];
  options: {
    takeaway: boolean;
    delivery: boolean;
    pickup: boolean;
    dine_in: boolean;
    tables_count: number;
    product_images: boolean;
    full_barcode_scan: boolean;
    combine_qty_on_scan: boolean;
    block_negative_stock: boolean;
    staff_on_lines: boolean;
  };
}

export class SetupApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(code);
    this.name = 'SetupApiError';
  }
}

async function call<T>(base: string, path: string, init: RequestInit, token?: string | null): Promise<T> {
  const headers: Record<string, string> = {};
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${base}/api/v1/setup${path}`, { ...init, headers });
  } catch {
    throw new SetupApiError(0, 'NETWORK');
  }
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new SetupApiError(res.status, body.error ?? `HTTP_${res.status}`);
  return body as T;
}

export const setupApi = {
  /** Admin app only: a setup token for re-running the wizard (admin session bearer). */
  reconfigure: (base: string, adminToken: string) =>
    call<{ setup_token: string }>(base, '/reconfigure', { method: 'POST' }, adminToken),
  auth: (base: string, tenant_slug: string, username: string, password: string) =>
    call<{ setup_token: string }>(base, '/auth', {
      method: 'POST',
      body: JSON.stringify({ tenant_slug, username, password }),
    }),
  state: (base: string, token: string) => call<SetupStateResponse>(base, '/state', { method: 'GET' }, token),
  saveDraft: (base: string, token: string, draft: unknown) =>
    call<{ ok: true }>(base, '/draft', { method: 'PUT', body: JSON.stringify({ draft }) }, token),
  complete: (base: string, token: string, payload: SetupCompletePayload) =>
    call<{ ok: true; business_type: string; modules: string[]; warnings: string[] }>(
      base,
      '/complete',
      { method: 'POST', body: JSON.stringify(payload) },
      token,
    ),
};

/** Downscale an uploaded logo to a PNG data URL (same as the admin Branding screen). */
export function fileToLogoDataUrl(file: File, maxDim = 512): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('READ_FAILED'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('DECODE_FAILED'));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('CANVAS_UNAVAILABLE'));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}
